import { useLocation, useNavigate, useSearchParams } from "@solidjs/router";
import { dispatchGlobalEvent } from "@storytell/events";
import { createEffect, createMemo, createSignal, on } from "solid-js";
import type { UIStore } from "../uiService";

type NavigationHistoryEntry = {
  mainId: string | undefined;
  sideId: string | undefined;
};

const ID_PREFIXES = [
  "chat",
  "asset",
  "view",
  "concept",
  "label",
  "project",
  "block",
  "skill",
  "action",
  "agent",
] as const;
export type IdPrefix = (typeof ID_PREFIXES)[number];

/** Page slugs are static routes that don't require an entity ID */
const PAGE_SLUGS = [
  "assets",
  "concepts",
  "labels",
  "collections",
  "skills",
  "personas",
  "actions",
  "memories",
  "new-collection",
  "chats",
  "search",
  "scope-picker",
  "record",
  "command-center",
  "live-sessions",
] as const;
export type PageSlug = (typeof PAGE_SLUGS)[number];

export const getIdPrefix = (id: string): IdPrefix | undefined => {
  const prefix = id.split("_")[0];
  return ID_PREFIXES.includes(prefix as IdPrefix) ? (prefix as IdPrefix) : undefined;
};

export const isPageSlug = (value: string): value is PageSlug => {
  return PAGE_SLUGS.includes(value as PageSlug);
};

/** Storage key for side panel disabled preference (must match apps/bluesky hook) */
const SIDE_PANEL_DISABLED_KEY = "side-panel-disabled";

/**
 * Checks if side panel is disabled via user preference.
 * When disabled, content should open in main panel instead of side panel.
 */
const isSidePanelDisabled = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const value = localStorage.getItem(SIDE_PANEL_DISABLED_KEY);
    return value === "true";
  } catch {
    return false;
  }
};

/**
 * Checks if a page ID represents a page that displays an assets list.
 * This includes:
 * - The main "assets" page
 * - Label detail pages (label_xxx) which show assets with that label
 * - Collection/View detail pages (view_xxx) which show filtered assets
 */
const isAssetsListPage = (id: string | undefined): boolean => {
  if (!id) return false;
  return id === "assets" || id.startsWith("label_") || id.startsWith("view_");
};

/**
 * Legacy URL patterns that should be redirected to the new format.
 * Old: /project/:projectId/chat/:chatId
 * New: /project/:projectId/:chatId
 */
const LEGACY_URL_PATTERNS = [
  {
    pattern: /^\/project\/([^/]+)\/chat\/([^/?]+)/,
    extract: (m: RegExpMatchArray) => ({ projectId: m[1], primaryId: m[2] }),
  },
] as const;

/**
 * Parses a URL pathname to extract navigation state.
 * Handles legacy patterns (e.g., /project/:projectId/chat/:chatId)
 * and the new format (/project/:projectId/:primaryId).
 */
const parseNavigationPath = (
  pathname: string
): {
  projectId: string | undefined;
  primaryId: string | undefined;
} => {
  // Check for legacy URL patterns first (e.g., /chat/:chatId segment)
  for (const { pattern, extract } of LEGACY_URL_PATTERNS) {
    const match = pathname.match(pattern);
    if (match) {
      const { projectId, primaryId } = extract(match);
      return { projectId, primaryId };
    }
  }

  // Action execution route: /project/:projectId/actions/:actionId/executions/:chatId
  // Extract chatId as primaryId to render the chat
  const executionMatch = pathname.match(/^\/project\/([^/]+)\/actions\/[^/]+\/executions\/([^/?]+)/);
  if (executionMatch) {
    return {
      projectId: executionMatch[1],
      primaryId: executionMatch[2],
    };
  }

  // New format: /project/:projectId/:primaryId
  const newFormatMatch = pathname.match(/^\/project\/([^/]+)\/([^/?]+)/);
  if (newFormatMatch) {
    return {
      projectId: newFormatMatch[1],
      primaryId: newFormatMatch[2],
    };
  }

  // Just project: /project/:projectId
  const projectOnlyMatch = pathname.match(/^\/project\/([^/]+)\/?$/);
  if (projectOnlyMatch) {
    return {
      projectId: projectOnlyMatch[1],
      primaryId: undefined,
    };
  }

  return { projectId: undefined, primaryId: undefined };
};

/**
 * Builds a navigation URL from the given parameters.
 * All entity types use the same format: /project/:projectId/:entityId
 */
const buildNavigationUrl = (params: {
  projectId: string;
  primaryId?: string;
  sideId?: string;
  sideHidden?: boolean;
  /** When side=scope-picker, open on "sources", "sections", or "prompts" tab */
  panelTab?: "sources" | "sections" | "prompts" | "activity" | "content";
  /** Source action ID when navigating to an execution chat */
  actionCtx?: string;
  /** When side=scope-picker with panelTab=content, the block to display */
  contentBlock?: string;
}): string => {
  const { projectId, primaryId, sideId, sideHidden, panelTab, actionCtx, contentBlock } = params;

  let url = `/project/${projectId}`;

  if (primaryId) {
    url += `/${primaryId}`;
  }

  const queryParams: string[] = [];
  if (sideId) {
    queryParams.push(`side=${sideId}`);
    if (sideHidden) {
      queryParams.push("sideHidden=true");
    }
  }
  if (panelTab) {
    queryParams.push(`panelTab=${panelTab}`);
  }
  if (actionCtx) {
    queryParams.push(`actionCtx=${encodeURIComponent(actionCtx)}`);
  }
  if (contentBlock) {
    queryParams.push(`contentBlock=${encodeURIComponent(contentBlock)}`);
  }

  if (queryParams.length > 0) {
    url += `?${queryParams.join("&")}`;
  }

  return url;
};

/**
 * Builds a URL rooted at "/" preserving existing query params while setting/removing side panel params.
 */
const buildRootUrl = (
  currentSearch: string,
  params?: { side?: string; sideHidden?: boolean; panelTab?: string; contentBlock?: string }
): string => {
  const searchParams = new URLSearchParams(currentSearch);

  if (params?.side) {
    searchParams.set("side", params.side);
    if (params.sideHidden) {
      searchParams.set("sideHidden", "true");
    } else {
      searchParams.delete("sideHidden");
    }
  } else {
    searchParams.delete("side");
    searchParams.delete("sideHidden");
  }

  if (params?.panelTab) {
    searchParams.set("panelTab", params.panelTab);
  }

  if (params?.contentBlock) {
    searchParams.set("contentBlock", encodeURIComponent(params.contentBlock));
  }

  const qs = searchParams.toString();
  return qs ? `/?${qs}` : "/";
};

type NavigationState = {
  projectId: string | undefined;
  primaryId: string | undefined;
  sideId: string | undefined;
  /** Side panel has content but is visually hidden by user */
  sideHidden: boolean;
  /** Side panel content should open in edit mode (for artifacts) */
  edit: boolean;
  /** When side=scope-picker, block ID to show in the "Edit Answer" tab */
  editBlock?: string;
  /** When navigating from an action execution, the source action ID */
  actionCtx?: string;
};

export type NavigationService = ReturnType<typeof createNavigationService>;

/**
 * Creates a navigation service that manages two-column navigation state via URL.
 *
 * URL Structure: /project/:projectId/:primaryId?side=:secondaryId
 *
 * - Primary column content is always in the path (required when navigating)
 * - Side panel content is in the `side` query parameter (optional)
 * - IDs are self-describing with prefixes (chat_, asset_, etc.)
 *
 * Legacy URLs like /project/:projectId/chat/:chatId are automatically
 * normalized to /project/:projectId/:chatId on navigation.
 */
export const createNavigationService = (deps: {
  ui: UIStore;
  // URL-first project id, falling back to /context only when the URL has no
  // project. Undefined when no project is reachable.
  currentProjectId: () => string | undefined;
  // Org id used as navigateHome's last-resort redirect target when no project
  // is reachable. Must already be access-guarded: /orgs/{id} is owner-only, so
  // a target the user can't access produces a false access-denied screen
  // (ENG-6478). Returns undefined to fall through to "/".
  homeOrgId: () => string | undefined;
  onSidePanelAction: (action: "open" | "hide" | "open_full") => void;
}) => {
  const { ui, currentProjectId, homeOrgId, onSidePanelAction } = deps;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams<{
    side?: string;
    sideHidden?: string;
    edit?: string;
    editBlock?: string;
    panelTab?: string;
    actionCtx?: string;
    contentBlock?: string;
  }>();

  const mergeSearchParams = (
    overrides: Partial<
      Record<
        "side" | "sideHidden" | "edit" | "editBlock" | "panelTab" | "actionCtx" | "contentBlock",
        string | undefined
      >
    >
  ): Record<string, string | undefined> => ({
    ...searchParams,
    ...overrides,
  });

  const state = createMemo((): NavigationState => {
    const parsed = parseNavigationPath(location.pathname);
    return {
      projectId: parsed.projectId,
      primaryId: parsed.primaryId,
      sideId: searchParams.side,
      sideHidden: searchParams.sideHidden === "true",
      edit: searchParams.edit === "true",
      editBlock: searchParams.editBlock,
      actionCtx: searchParams.actionCtx,
    };
  });

  const [history, setHistory] = createSignal<NavigationHistoryEntry[]>([]);
  let lastRecordedMainId: string | undefined;
  let lastRecordedSideId: string | undefined;
  let isInitialized = false;

  createEffect(() => {
    const currentMainId = state().primaryId;
    const currentSideId = state().sideId;

    if (!isInitialized) {
      isInitialized = true;
      lastRecordedMainId = currentMainId;
      lastRecordedSideId = currentSideId;
      return;
    }

    if (currentMainId === lastRecordedMainId && currentSideId === lastRecordedSideId) {
      return;
    }

    // Check if new state matches the top of our history stack (indicates back navigation)
    const currentHistory = history();
    const lastEntry = currentHistory.length > 0 ? currentHistory[currentHistory.length - 1] : undefined;

    if (lastEntry && lastEntry.mainId === currentMainId && lastEntry.sideId === currentSideId) {
      setHistory((h) => h.slice(0, -1));
    } else {
      const previousEntry: NavigationHistoryEntry = {
        mainId: lastRecordedMainId,
        sideId: lastRecordedSideId,
      };
      setHistory((h) => [...h, previousEntry]);
    }

    lastRecordedMainId = currentMainId;
    lastRecordedSideId = currentSideId;
  });

  const canGoBack = (): boolean => history().length > 0;

  /**
   * Navigates back to the previous state in our history.
   * Uses browser history.back() to maintain sync with browser navigation.
   * Returns true if navigation occurred, false if no history.
   */
  const goBack = (): boolean => {
    if (!canGoBack()) return false;
    window.history.back();
    return true;
  };

  // Falls back to currentProjectId() so navigation works from pages without a
  // projectId in the URL (homepage, `/orgs/{orgId}/...`).
  const getProjectId = (overrideProjectId?: string): string | undefined => {
    return overrideProjectId ?? state().projectId ?? currentProjectId();
  };

  /** Closes the side panel unless explicitly preserved. */
  const navigateToPrimary = (
    primaryId: string,
    options?: {
      projectId?: string;
      preserveSidePanel?: boolean;
      /** Source action ID when navigating to an execution chat */
      actionCtx?: string;
    }
  ) => {
    const projectId = getProjectId(options?.projectId);
    if (!projectId) {
      return;
    }

    const currentState = state();
    const url = buildNavigationUrl({
      projectId,
      primaryId,
      sideId: options?.preserveSidePanel ? currentState.sideId : undefined,
      sideHidden: options?.preserveSidePanel ? currentState.sideHidden : undefined,
      actionCtx: options?.actionCtx,
    });
    navigate(url);
  };

  /**
   * Opens content in the side panel, always un-hiding if hidden.
   * If the same content is already visible in the side panel, dispatches a highlight event.
   * If user has disabled side panel in settings, opens in main panel instead.
   * If there's no primary content, opens in side panel with implicit "new" chat state.
   */
  const openSidePanel = (
    sideId: string,
    options?: {
      projectId?: string;
      panelTab?: "sources" | "sections" | "prompts" | "activity" | "content";
      contentBlock?: string;
    }
  ) => {
    const pathname = location.pathname;
    // If side panel is disabled by user preference, open in main panel instead
    // Exception: scope-picker ("powering this chat") always opens in side panel
    if (isSidePanelDisabled() && sideId !== "scope-picker") {
      navigateToPrimary(sideId, { projectId: options?.projectId });
      return;
    }

    // When command center is docked in sidepanel, redirect other content to main panel
    // to avoid displacing the docked command center
    if (state().sideId === "command-center" && sideId !== "command-center") {
      navigateToPrimary(sideId, { projectId: options?.projectId, preserveSidePanel: true });
      return;
    }

    const currentState = state();
    const projectId = options?.projectId ?? currentState.projectId ?? currentProjectId();
    const { primaryId } = currentState;

    // If side panel is already showing this content and is visible, either switch tab or just highlight
    if (currentState.sideId === sideId && !currentState.sideHidden) {
      if (options?.panelTab) {
        const mergeParams: Parameters<typeof mergeSearchParams>[0] = { panelTab: options.panelTab };
        if (options?.contentBlock) mergeParams.contentBlock = options.contentBlock;
        setSearchParams(mergeSearchParams(mergeParams));
        return;
      }
      dispatchGlobalEvent("highlight-side-panel", undefined);
      return;
    }

    onSidePanelAction("open");
    const searchOverrides: Parameters<typeof mergeSearchParams>[0] = { side: sideId, sideHidden: undefined };
    if (options?.panelTab) searchOverrides.panelTab = options.panelTab;
    if (options?.contentBlock) searchOverrides.contentBlock = options.contentBlock;

    // The root URL carries no projectId, so the panel lives entirely in search params.
    if (pathname === "/") {
      setSearchParams(mergeSearchParams(searchOverrides));
      ui.layout.setSideMinimized(false);
      navigate(
        buildRootUrl(location.search, {
          side: sideId,
          sideHidden: false,
          panelTab: options?.panelTab,
          contentBlock: options?.contentBlock,
        })
      );
      return;
    }

    if (!projectId) {
      return;
    }

    // If no primary content, open side panel from project root (implicit "new" chat state)
    if (!primaryId) {
      setSearchParams(mergeSearchParams(searchOverrides));
      ui.layout.setSideMinimized(false);
      const url = buildNavigationUrl({
        projectId,
        sideId,
        panelTab: options?.panelTab,
        actionCtx: currentState.actionCtx,
        contentBlock: options?.contentBlock,
      });
      navigate(url);
      return;
    }

    ui.layout.setSideMinimized(false);
    const url = buildNavigationUrl({
      projectId,
      primaryId,
      sideId,
      panelTab: options?.panelTab,
      actionCtx: currentState.actionCtx,
      contentBlock: options?.contentBlock,
    });
    navigate(url);
  };

  /**
   * Hides the side panel visually but keeps the content reference in URL.
   * The panel can be restored by calling showSidePanel() or openSidePanel().
   */
  const hideSidePanel = () => {
    const { projectId, sideId, sideHidden } = state();
    // The root URL carries no projectId, so the panel lives entirely in search params.
    if (!sideId || (location.pathname !== "/" && !projectId)) return;
    // Effects re-call this on every URL change while the panel stays hidden; count only the transition.
    if (!sideHidden) onSidePanelAction("hide");
    setSearchParams(mergeSearchParams({ side: sideId, sideHidden: "true" }));
    ui.layout.setSideMinimized(true);
  };

  /**
   * Shows the side panel if it was hidden, without changing its content.
   */
  const showSidePanel = () => {
    const currentState = state();
    if (location.pathname === "/" && currentState.sideId) {
      ui.layout.setSideMinimized(false);
      navigate(
        buildRootUrl(location.search, {
          side: currentState.sideId,
          sideHidden: false,
          panelTab: searchParams.panelTab,
          contentBlock: searchParams.contentBlock,
        })
      );
      return;
    }

    const { projectId, sideId } = currentState;
    if (!projectId || !sideId) {
      return;
    }
    ui.layout.setSideMinimized(false);
    // Query-only: rebuilding the URL from primaryId would drop the rest of a nested path, such as
    // the session id under /live-sessions.
    setSearchParams(mergeSearchParams({ sideHidden: undefined }));
  };

  /**
   * Completely closes the side panel, removing the content reference from URL.
   */
  const closeSidePanel = () => {
    if (location.pathname === "/") {
      navigate(buildRootUrl(location.search));
      return;
    }

    const { projectId, primaryId, actionCtx } = state();
    if (!projectId) return;

    const url = buildNavigationUrl({ projectId, primaryId, actionCtx });
    navigate(url);
  };

  /**
   * Smart navigation that considers page types.
   * - When in side panel: opens in side panel (replaces side content), except "new" chat opens in main
   * - When in main on an assets list page and target is also assets list page: opens in main
   * - Otherwise: opens in side panel
   *
   * @param targetId - The ID to navigate to
   * @param isInSidePanel - Whether the calling component is in the side panel
   */
  const navigateToContent = (targetId: string, isInSidePanel: boolean) => {
    const { primaryId } = state();

    if (isInSidePanel) {
      if (targetId === "new") {
        navigateToPrimary(targetId, { preserveSidePanel: true });
      } else {
        openSidePanel(targetId);
      }
    } else if (isAssetsListPage(primaryId) && isAssetsListPage(targetId)) {
      navigateToPrimary(targetId);
    } else {
      openSidePanel(targetId);
    }
  };

  /** Closes the side panel and navigates to its content as primary. */
  const promoteSidePanel = () => {
    const { projectId, sideId } = state();
    if (!projectId || !sideId) {
      return;
    }
    onSidePanelAction("open_full");

    const url = buildNavigationUrl({ projectId, primaryId: sideId });
    navigate(url);
  };

  const navigateToProject = (projectId: string, primaryId?: string) => {
    const url = buildNavigationUrl({ projectId, primaryId });
    navigate(url);
  };

  /**
   * Navigate to a page slug (static route) within the current project.
   * Example: navigateToPage("assets") -> /project/:projectId/assets
   */
  const navigateToPage = (page: PageSlug, options?: { projectId?: string }) => {
    const projectId = getProjectId(options?.projectId);
    if (!projectId) {
      return;
    }

    const url = `/project/${projectId}/${page}`;
    navigate(url);
  };

  const navigateHome = () => {
    const projectId = getProjectId();
    if (projectId) {
      navigate(`/project/${projectId}`);
      return;
    }
    const orgId = homeOrgId();
    if (orgId) {
      navigate(`/orgs/${orgId}`);
      return;
    }
    navigate("/");
  };

  const isSideHidden = () => state().sideHidden;

  // Sync layout collapsed state with URL - side panel is collapsed when no sideId in URL
  createEffect(
    on(
      () => searchParams.side,
      (sideId) => {
        ui.layout.setSideCollapsed(!sideId);
      },
      { defer: false }
    )
  );

  // Sync layout minimized state with URL - side panel is minimized when sideHidden=true
  createEffect(
    on(
      () => searchParams.sideHidden,
      (sideHidden) => {
        const minimized = sideHidden === "true";
        ui.layout.setSideMinimized(minimized);
      },
      { defer: false }
    )
  );

  /** Clear edit block and edit mode from side panel URL state */
  const clearSidePanelEditBlock = () => {
    setSearchParams(mergeSearchParams({ editBlock: undefined, edit: undefined }), { replace: true });
  };

  // The action context is stored in the URL as ?actionCtx=action_xxx so it
  // survives page refreshes and avoids SolidJS router async transition timing
  // issues with signal-based state.

  const executionContext = createMemo(() => {
    const actionId = state().actionCtx;
    if (actionId) return { actionId };
    return null;
  });

  // Encodes action ID in URL for breadcrumb lineage: Project > Actions > Action Name > Chat Title
  const navigateToExecution = (chatId: string, actionId: string, options?: { projectId?: string }) => {
    const currentState = state();
    // Preserve side panel (e.g. docked command-center) so the docking effect
    // doesn't push a second history entry that breaks the back button.
    const preserveSidePanel = currentState.sideId === "command-center";
    navigateToPrimary(chatId, {
      projectId: options?.projectId,
      preserveSidePanel,
      actionCtx: actionId,
    });
  };

  return {
    state,
    isSideHidden,

    navigateToPrimary,
    navigateToExecution,
    executionContext,
    navigateToProject,
    navigateToPage,
    navigateToContent,
    navigateHome,
    openSidePanel,
    clearSidePanelEditBlock,
    hideSidePanel,
    showSidePanel,
    closeSidePanel,
    promoteSidePanel,

    canGoBack,
    goBack,

    buildUrl: buildNavigationUrl,
  };
};
